import {
  PLUGIN_AUTH_PROVIDERS,
  PLUGIN_IS_PASSWORD_USER,
  PLUGIN_LDAP_FORM_FIELDS,
  PLUGIN_REDUX_MIDDLEWARES,
} from "metabase/plugins";
import type { AuthProvider } from "metabase/plugins/types";
import { LOGIN, LOGIN_GOOGLE } from "metabase/redux/auth";
import MetabaseSettings from "metabase/utils/settings";
import { hasPremiumFeature } from "metabase-enterprise/settings";
import type { OidcAuthProvider, User } from "metabase-types/api";

import { createSessionMiddleware } from "../auth/middleware/session-middleware";

import { AuthSettingsPage } from "./components/AuthSettingsPage";
import {
  LdapGroupMembershipFilter,
  LdapUserProvisioning,
} from "./components/Ldap";
import { createOidcAuthProvider } from "./components/OidcButton/OidcButton";
import { SsoButton } from "./components/SsoButton";

const settingsSAMLForm = () =>
  import(
    /* webpackChunkName: "auth-saml" */ "./components/SettingsSAMLForm"
  ).then(({ SettingsSAMLForm }) => ({
    Component: SettingsSAMLForm,
  }));

const settingsJWTForm = () =>
  import(
    /* webpackChunkName: "auth-jwt" */ "./components/SettingsJWTForm"
  ).then(({ SettingsJWTForm }) => ({
    Component: SettingsJWTForm,
  }));

const settingsOIDCForm = () =>
  import(
    /* webpackChunkName: "auth-oidc" */ "./components/SettingsOIDCForm"
  ).then(({ SettingsOIDCForm }) => ({
    Component: SettingsOIDCForm,
  }));

const SSO_PROVIDER = {
  name: "sso",
  Button: SsoButton,
};

// Always set AuthSettingsPage - this doesn't depend on premium features
PLUGIN_AUTH_PROVIDERS.AuthSettingsPage = AuthSettingsPage;

/**
 * Initialize auth plugin features that depend on hasPremiumFeature.
 */
export function initializePlugin() {
  if (hasPremiumFeature("sso_saml")) {
    PLUGIN_AUTH_PROVIDERS.settingsSAMLForm = settingsSAMLForm;
  }

  if (hasPremiumFeature("sso_jwt")) {
    PLUGIN_AUTH_PROVIDERS.settingsJWTForm = settingsJWTForm;
  }

  if (hasPremiumFeature("sso_oidc")) {
    PLUGIN_AUTH_PROVIDERS.settingsOIDCForm = settingsOIDCForm;
  }

  // Add provider function that handles SSO and password login
  const enhancedProviderFunction = (providers: AuthProvider[]) => {
    if (
      (hasPremiumFeature("sso_jwt") || hasPremiumFeature("sso_saml")) &&
      MetabaseSettings.get("other-sso-enabled?")
    ) {
      providers = [SSO_PROVIDER, ...providers];
    }

    // Add OIDC provider buttons
    if (hasPremiumFeature("sso_oidc")) {
      const oidcProviders: OidcAuthProvider[] =
        MetabaseSettings.get("oidc-login-providers") ?? [];
      for (const oidcProvider of oidcProviders) {
        providers = [createOidcAuthProvider(oidcProvider), ...providers];
      }
    }

    if (
      hasPremiumFeature("disable_password_login") &&
      !MetabaseSettings.isPasswordLoginEnabled() &&
      !MetabaseSettings.isLdapEnabled()
    ) {
      providers = providers.filter((p) => p.name !== "password");
    }
    return providers;
  };

  PLUGIN_AUTH_PROVIDERS.providers.push(enhancedProviderFunction);

  if (hasPremiumFeature("disable_password_login")) {
    const passwordUserFunction = (user: User) =>
      Boolean(
        user.sso_source !== "google" &&
        user.sso_source !== "ldap" &&
        user.sso_source !== "oidc" &&
        MetabaseSettings.isPasswordLoginEnabled(),
      );

    PLUGIN_IS_PASSWORD_USER.push(passwordUserFunction);
  }

  if (hasPremiumFeature("sso_ldap")) {
    Object.assign(PLUGIN_LDAP_FORM_FIELDS, {
      LdapGroupMembershipFilter,
      LdapUserProvisioning,
    });
  }

  if (hasPremiumFeature("session_timeout_config")) {
    PLUGIN_REDUX_MIDDLEWARES.push(
      createSessionMiddleware([LOGIN, LOGIN_GOOGLE]),
    );
  }
}
